import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  Observable,
  catchError,
  concat,
  from,
  map,
  of,
  switchMap,
  toArray,
  withLatestFrom,
} from 'rxjs';

import { DivisionsApiService } from '@core/data/api/divisions-api.service';
import { divisionChanged, divisionFromDto, divisionToDto } from '@core/data/api/divisions.mapper';
import { DataRepository } from '@core/data/data-repository';
import { Division } from '@core/models';

import { DivisionsApiActions, DivisionsPageActions } from './divisions.actions';
import { selectDivisions } from './divisions.reducer';

/**
 * Bridges divisions actions to the data layer. Effects own all async work and
 * error handling so reducers stay pure and synchronous.
 */
@Injectable()
export class DivisionsEffects {
  private readonly actions$ = inject(Actions);
  private readonly repo = inject(DataRepository);
  private readonly api = inject(DivisionsApiService);
  private readonly store = inject(Store);

  readonly load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DivisionsPageActions.entered),
      switchMap(() =>
        this.divisions$().pipe(
          map((divisions) => DivisionsApiActions.loadSuccess({ divisions })),
          catchError((err: unknown) =>
            of(DivisionsApiActions.loadFailure({ error: toMessage(err) })),
          ),
        ),
      ),
    ),
  );

  /**
   * Saving divisions.
   *
   * The page edits the WHOLE list and dispatches it, which is how this screen has
   * always worked — but the API is per row. So the list is reconciled against what
   * the store currently holds: anything new is a POST, anything changed is a PUT,
   * and anything gone is a DELETE.
   *
   * Order matters in one place. Removals go LAST, so a refused delete cannot also
   * lose the additions made in the same edit.
   *
   * A division is matched on `id` — their `code` — which is the identity. A PUT may
   * change the code, and the path carries the one it had while the body carries the
   * one it is getting, so a renumber is an update rather than a delete and an add.
   */
  readonly save$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DivisionsPageActions.saved),
      // The list as the store holds it right now, paired with the one being saved:
      // the reconciliation needs both, and `withLatestFrom` is how an effect reads
      // state without subscribing by hand.
      withLatestFrom(this.store.select(selectDivisions)),
      switchMap(([{ divisions }, before]) => {
        const byId = new Map(before.map((d) => [d.id, d]));
        const writes: Observable<unknown>[] = [];
        for (const next of divisions) {
          const prev = byId.get(next.id);
          if (!prev) writes.push(this.api.createDivision(divisionToDto(next)));
          else if (divisionChanged(prev, next)) {
            writes.push(this.api.replaceDivision(prev.id, divisionToDto(next)));
          }
        }
        const keep = new Set(divisions.map((d) => d.id));
        for (const gone of before.filter((d) => !keep.has(d.id))) {
          writes.push(this.api.retireDivision(gone.id));
        }
        // Nothing changed — do not round-trip for the sake of it.
        if (!writes.length) return of(DivisionsApiActions.saveSuccess({ divisions }));
        return concat(...writes).pipe(
          toArray(),
          // Re-read rather than trusting the list we sent: the server owns what a
          // division looks like afterwards, retirement included.
          switchMap(() => this.divisions$()),
          map((saved) => DivisionsApiActions.saveSuccess({ divisions: saved })),
          catchError((err: unknown) =>
            of(DivisionsApiActions.saveFailure({ error: toMessage(err) })),
          ),
        );
      }),
    ),
  );

  /**
   * The list, from the server. There is no seeded alternative — divisions are
   * wired, so their seed was deleted (standards §8c).
   */
  private divisions$(): Observable<Division[]> {
    return this.api.listDivisions().pipe(map((rows) => rows.map(divisionFromDto)));
  }
}

function toMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}
